import {
  addKeyPersonRelationship,
  createKeyPerson,
  personsInCompanyKeys,
  personsInEntityKeys,
  queryClient,
  updateKeyPerson,
  usePersonsInCompany,
  usePersonsInEntity,
} from '@commenda/api';
import {
  Button,
  Checkbox,
  ComboboxWithSearch,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  Input,
  InputErrorMessage,
  Label,
  Loading,
  LoadingIndicator,
  MultiSelect,
  PhoneInput,
  SingleCombobox,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { addressSchema } from '@commenda/schema/address';
import {
  BoardPositionEnum,
  GovernanceRole,
  PersonLocationType,
} from '@commenda/types';
import { getAllCountryOptions } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country, LocationType } from '@prisma/client';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import AddressGroupRHF from '../../../components/AddressGroupRHF';
import { useNotification } from '../../../hooks/useNotification';
import {
  getKeyPersonRoles,
  KEY_PERSON_ROLE_OPTIONS,
  KeyPersonRolesEnum,
} from '../utils';

const addKeyPersonFormSchema = z
  .object({
    firstName: z.string().trim().min(1, { message: 'First name is required' }),
    lastName: z.string().trim().optional().nullable(),
    email: z.string().email({ message: 'Please enter a valid email address' }),
    phoneNumber: z.string().refine((value) => isValidPhoneNumber(value), {
      message: 'Please enter a valid phone number',
    }),
    roles: z
      .array(z.nativeEnum(KeyPersonRolesEnum))
      .min(1, { message: 'At least one role is required' }),
    designation: z.string().trim().optional().nullable(),
    address: addressSchema,
    isAuthorizedSignatory: z.boolean().default(false),
    countryOfCitizenship: z.nativeEnum(Country).optional().nullable(),
    employmentStartDate: z.string().trim().optional().nullable(),
    ssn: z.string().trim().optional().nullable(),
    hasNoSSN: z.boolean().default(false),
    directorIdentificationNumber: z.string().trim().optional().nullable(),
    isResidentDirector: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.designation && data.designation.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Designation is required',
        path: ['designation'],
      });
    }
  });

type AddKeyPersonFormSchemaFields = z.infer<typeof addKeyPersonFormSchema>;

export const AddKeyPersonModal = ({
  isOpen,
  onClose,
  onSuccess,
  businessEntityId,
  companyId,
  alreadyExistingKeyPersonIds,
  isEditMode,
  personId,
  allowNameEditInEditMode = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  businessEntityId: number;
  companyId: number;
  alreadyExistingKeyPersonIds?: number[];
  isEditMode?: boolean;
  personId?: number;
  allowNameEditInEditMode?: boolean;
}) => {
  const { data: personsInCompany, error: personsInCompanyError } =
    usePersonsInCompany(companyId);
  const { data: personsInEntity, error: personsInEntityError } =
    usePersonsInEntity(businessEntityId, { includeSSN: true });

  const { setNotification } = useNotification();

  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);

  const persons =
    personsInCompany?.persons
      ?.filter((person) => !alreadyExistingKeyPersonIds?.includes(person.id))
      ?.map((person) => ({
        value: person.id.toString(),
        displayedValue: person.firstName,
      })) || [];

  const methods = useForm<AddKeyPersonFormSchemaFields>({
    resolver: zodResolver(addKeyPersonFormSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
    reset,
  } = methods;

  const close = () => {
    reset();
    setSelectedPersonId(null);
    onClose();
  };

  const isAddingOrEditingKeyPerson = useToggle();

  useEffect(() => {
    if (personId) setSelectedPersonId(personId);

    return () => setSelectedPersonId(null);
  }, [personId, isOpen]);

  useEffect(() => {
    if (selectedPersonId) {
      const existingPerson = personsInCompany?.persons.find(
        (person) => person.id === selectedPersonId
      );

      const existingPersonInEntity = personsInEntity?.persons.find(
        (person) => person.id === selectedPersonId
      );

      const existingPersonAddress = existingPerson?.locations?.find((address) =>
        address.type.includes(PersonLocationType.OPERATING)
      )?.address;

      const existingPersonRoles = getKeyPersonRoles(existingPersonInEntity);

      const isAuthorizedSignatory =
        !!existingPersonInEntity?.governanceRelationships?.[
          GovernanceRole.AUTHORIZED_SIGNATORY
        ]?.isAuthorizedSignatory;

      // Get SSN info from personsInEntity (which has includeSSN: true for decryptedValue)
      const ssnKyc = existingPersonInEntity?.kycIds?.find(
        (kyc) => kyc.type === 'US_SSN'
      );

      reset({
        firstName: existingPerson?.firstName ?? '',
        lastName: existingPerson?.lastName ?? '',
        email: existingPerson?.email ?? '',
        phoneNumber: existingPerson?.phoneNumber ?? '',
        roles: existingPersonRoles,
        designation: existingPersonInEntity?.jobTitle?.join(', ') ?? '',
        address: {
          addressLine1: existingPersonAddress?.addressLine1 ?? '',
          addressLine2: existingPersonAddress?.addressLine2 ?? '',
          city: existingPersonAddress?.city ?? '',
          postalCode: existingPersonAddress?.postalCode ?? '',
          state: existingPersonAddress?.state ?? '',
          country: (existingPersonAddress?.country ?? '') as Country,
        },
        isAuthorizedSignatory,
        countryOfCitizenship: (existingPerson?.nationalities?.[0] ??
          null) as Country | null,
        employmentStartDate:
          existingPerson?.employmentStartDate ||
          existingPersonInEntity?.startDateOfEmployment ||
          '',
        ssn: ssnKyc?.decryptedValue || '',
        hasNoSSN: ssnKyc?.doesNotHaveKYCId ?? false,
        directorIdentificationNumber:
          existingPersonInEntity?.directorIdentificationNumber ??
          existingPerson?.directorIdentificationNumber ??
          '',
        isResidentDirector: existingPersonInEntity?.isResidentDirector ?? false,
      });
    } else {
      reset({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        roles: [],
        designation: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          postalCode: '',
          state: '',
          country: '',
        },
        isAuthorizedSignatory: false,
        countryOfCitizenship: null,
        employmentStartDate: '',
        ssn: '',
        hasNoSSN: false,
        directorIdentificationNumber: '',
        isResidentDirector: false,
      });
    }
  }, [selectedPersonId, isOpen]);

  const onSubmit = async (data: AddKeyPersonFormSchemaFields) => {
    isAddingOrEditingKeyPerson.on();

    const baseBody = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
      locations: [
        {
          locationTypes: [LocationType.CONTACT_ADDRESS],
          address: {
            addressLine1: data.address.addressLine1,
            addressLine2: data.address.addressLine2,
            city: data.address.city,
            postalCode: data.address.postalCode,
            state: data.address.state,
            country: data.address.country as Country,
          },
        },
      ],
      ...(data.countryOfCitizenship && {
        countryOfCitizenship: data.countryOfCitizenship,
      }),
      ...(data.employmentStartDate && {
        employmentStartDate: data.employmentStartDate,
      }),
      // Send SSN value or null if hasNoSSN is checked (to clear existing SSN)
      ...(data.hasNoSSN ? { ssn: null } : data.ssn ? { ssn: data.ssn } : {}),
      hasNoSSN: data.hasNoSSN,
      ...(data.directorIdentificationNumber
        ? { directorIdentificationNumber: data.directorIdentificationNumber }
        : {}),
    };

    const createOrUpdatePersonResponse = selectedPersonId
      ? await updateKeyPerson(selectedPersonId, baseBody)
      : await createKeyPerson({
          companyId,
          ...baseBody,
        });

    if (!createOrUpdatePersonResponse.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong while adding the key person, please try again or try again or contact Team Commenda if you need help.',
      });
      isAddingOrEditingKeyPerson.off();
      return;
    }

    const createdOrUpdatedPersonId = (await createOrUpdatePersonResponse.json())
      .id;

    const roles = data.roles.filter(
      (role) => role !== KeyPersonRolesEnum.DIRECTOR
    );

    const addKeyPersonRelationshipResponse = await addKeyPersonRelationship(
      businessEntityId,
      createdOrUpdatedPersonId,
      {
        ...(data.designation ? { jobTitle: [data.designation] } : {}),
        isAuthorizedSignatory: data.isAuthorizedSignatory,
        isDirector: data.roles.includes(KeyPersonRolesEnum.DIRECTOR),
        roles: roles.map((role) => role as unknown as BoardPositionEnum),
        isResidentDirector: data.isResidentDirector,
      }
    );

    if (!addKeyPersonRelationshipResponse.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong while adding the key person, please try again or try again or contact Team Commenda if you need help.',
      });
      isAddingOrEditingKeyPerson.off();
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: [
        personsInEntityKeys.FETCH_PERSONS_IN_ENTITY,
        { businessEntityId },
      ],
    });

    await queryClient.invalidateQueries({
      queryKey: [personsInCompanyKeys.FETCH_PERSONS_IN_COMPANY, +companyId],
    });

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: isEditMode
        ? 'Key person updated successfully.'
        : 'Key person added successfully.',
    });

    mixpanel.track('ENTITY_KEY_PERSON_ADDED', {
      source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
      surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
      company_id: companyId,
      is_edit: Boolean(isEditMode),
    });

    isAddingOrEditingKeyPerson.off();
    await onSuccess();
    close();
  };

  if (!personsInCompany || !personsInEntity) {
    if (personsInCompanyError) console.error(personsInCompanyError);
    if (personsInEntityError) console.error(personsInEntityError);

    return (
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent className="overflow-y-hidden" preventCloseOnOutsideClick>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit key person' : 'Add key person'}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[400px] w-full flex justify-center items-center">
            <Loading />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="overflow-y-hidden" preventCloseOnOutsideClick>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit key person' : 'Add key person'}
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-2xl p-3xl overflow-y-auto h-[60vh] md-h:h-[540px]">
              <FieldWrapper>
                <Label>First name</Label>
                <ComboboxWithSearch
                  inputValue={watch('firstName')}
                  setInputValue={(val) => {
                    clearErrors('firstName');
                    setSelectedPersonId(+val);
                  }}
                  inputClassName="h-10 txt-small-regular"
                  disabled={isEditMode && !allowNameEditInEditMode}
                  options={persons}
                  error={errors?.firstName?.message}
                  placeholder="Enter first name"
                  onChangeInputValue={(val: string) => {
                    clearErrors('firstName');
                    setValue('firstName', val);
                    if (!isEditMode) {
                      setSelectedPersonId(null);
                    }
                  }}
                  hideAddOption
                  {...(isEditMode &&
                    !allowNameEditInEditMode && {
                      displayValueIfDisabled: watch('firstName'),
                    })}
                />
                {errors?.firstName?.message && (
                  <InputErrorMessage message={errors.firstName.message} />
                )}
              </FieldWrapper>
              <FieldWrapper>
                <Label>Last name</Label>
                <Input
                  disabled={isEditMode && !allowNameEditInEditMode}
                  placeholder="Enter last name"
                  error={errors?.lastName?.message}
                  value={watch('lastName')}
                  onChange={(e) => {
                    if (!isEditMode) {
                      setSelectedPersonId(null);
                    }
                    setValue('lastName', e.target.value);
                  }}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>Email</Label>
                <Input
                  {...register('email')}
                  placeholder="Email address"
                  error={errors?.email?.message}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>Phone number</Label>
                <PhoneInput
                  id="phoneNumber"
                  placeholder=""
                  className="h-[42px]"
                  onChange={(e) => {
                    setValue('phoneNumber', e);
                    clearErrors('phoneNumber');
                  }}
                  value={watch('phoneNumber')}
                  error={errors?.phoneNumber?.message}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label>
                  <span>Role&nbsp;</span>
                  <Tooltip content="Indicate if the person is the President, Secretary, Treasurer or a Director.">
                    <Icons.InfoCircle className="size-4 text-fg-tertiary" />
                  </Tooltip>
                </Label>
                <MultiSelect
                  options={KEY_PERSON_ROLE_OPTIONS}
                  value={watch('roles')}
                  onValueChange={(val: KeyPersonRolesEnum[]) => {
                    clearErrors('roles');
                    setValue('roles', val);
                  }}
                  defaultValue={watch('roles')}
                  maxCount={2}
                />
                {errors?.roles?.message && (
                  <InputErrorMessage message={errors.roles.message} />
                )}
              </FieldWrapper>
              <FieldWrapper>
                <Label>Designation (optional)</Label>
                <Input
                  {...register('designation')}
                  placeholder="Enter designation"
                  error={errors?.designation?.message}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label className="txt-medium-bold mb-sm">Address</Label>
                <AddressGroupRHF pathPrefix="address" />
              </FieldWrapper>

              <FieldWrapper>
                <div className="flex items-center gap-md">
                  <Checkbox
                    checked={watch('isAuthorizedSignatory')}
                    onCheckedChange={(val: boolean) => {
                      setValue('isAuthorizedSignatory', val);
                    }}
                  />
                  <p className="txt-medium-medium text-text-secondary">
                    This person is also an authorized signatory.
                  </p>
                </div>
              </FieldWrapper>

              <FieldWrapper>
                <Label>Country of Citizenship (optional)</Label>
                <SingleCombobox
                  options={getAllCountryOptions().map((country) => ({
                    value: country.code,
                    label: country.name,
                  }))}
                  value={watch('countryOfCitizenship') || ''}
                  setValue={(val) => {
                    setValue('countryOfCitizenship', val as Country);
                  }}
                  placeholder="Select country"
                  _className="txt-small-medium text-text-secondary h-10"
                />
              </FieldWrapper>

              <FieldWrapper>
                <Label>Start Date of Employment (optional)</Label>
                <DatePicker
                  date={watch('employmentStartDate') || undefined}
                  setDate={(date) => {
                    setValue('employmentStartDate', date || '');
                  }}
                  placeholder="Select date"
                />
              </FieldWrapper>

              <FieldWrapper>
                <Label>SSN/ITIN (optional)</Label>
                <Input
                  {...register('ssn')}
                  placeholder="Enter SSN or ITIN"
                  disabled={watch('hasNoSSN')}
                  error={errors?.ssn?.message}
                />
                <div className="flex items-center gap-md mt-sm">
                  <Checkbox
                    checked={watch('hasNoSSN')}
                    onCheckedChange={(val: boolean) => {
                      setValue('hasNoSSN', val, { shouldValidate: true });
                      if (val) {
                        setValue('ssn', null, { shouldValidate: true });
                        clearErrors('ssn');
                      }
                    }}
                  />
                  <p className="txt-small-medium text-text-secondary">
                    This person does not have an SSN/ITIN
                  </p>
                </div>
              </FieldWrapper>

              <FieldWrapper>
                <Label>Director Identification Number (optional)</Label>
                <Input
                  {...register('directorIdentificationNumber')}
                  placeholder="Enter DIN"
                  error={errors?.directorIdentificationNumber?.message}
                />
              </FieldWrapper>

              <FieldWrapper>
                <div className="flex items-center gap-md">
                  <Checkbox
                    checked={watch('isResidentDirector')}
                    onCheckedChange={(val: boolean) => {
                      setValue('isResidentDirector', val);
                    }}
                  />
                  <p className="txt-medium-medium text-text-secondary">
                    This person is a resident director of the entity.
                  </p>
                </div>
              </FieldWrapper>
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={close}
                size="sm"
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isAddingOrEditingKeyPerson.value}
              >
                {isAddingOrEditingKeyPerson.value ? (
                  <LoadingIndicator size={16} />
                ) : null}
                <span>Submit</span>
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
